<template>
  <div id="promotionEdit">

    <PageTitle :title="$to(promotion.name)" :subtitle="$t('promotion_new.edit_subtitle')" v-if="promotion"></PageTitle>

    <div class="container" v-if="promotion">
      <Tabs>
        <TabSection :title="$t('promotion_new.configuration')">
          <div class="subtitle">{{ $t('promotion_new.promotion_type') }}</div>
          <label>{{ $t('promotion_new.promotion_type_edit_subtitle') }}</label>
          <div class="promotion-types">
              <PromotionType :active="isXforY" @click="setAsXforY" :name="$t('promotion_new.x_for_y_title')"
                             :description="$t('promotion_new.x_for_y_description')" />
              <PromotionType :active="isProductDiscount" @click="setAsProductDiscount"
                             :name="$t('promotion_new.product_discount_title')"
                             :description="$t('promotion_new.product_discount_description')" />
              <PromotionType :active="isProductAccess" @click="setAsProductAccess"
                             :name="$t('promotion_new.product_access_title')"
                             :description="$t('promotion_new.product_access_description')" />
              <PromotionType :active="isOrderDiscount" @click="setAsOrderDiscount"
                             :name="$t('promotion_new.order_discount_title')"
                             :description="$t('promotion_new.order_discount_description')" />
          </div>

          <div class="separator"></div>
          <div class="subtitle">{{ $t('promotion_new.general_configuration') }}</div>
          <label>{{ $t('promotion_new.general_configuration_subtitle') }}</label>
          <br>
          <div class="columns">
            <template v-if="isXforY">
              <div class="column">
                <XYParameter :promotion="promotion" />
                <br>
                <MinimumAmount :promotion="promotion" :currencySymbol="currencySymbol" />
                <br>
                <Activation :promotion="promotion" @hasCode="hasCode = $event" />
              </div>
              <div class="column">
                <Tags :promotion="promotion" :tags="formattedTags" />
                <br>
                <UsageRestriction :promotion="promotion" />
                <br>
                <Application :promotion="promotion" />
              </div>
            </template>
            <template v-if="isProductAccess">
              <div class="column">
                <MinimumAmount :promotion="promotion" :currencySymbol="currencySymbol" />
                <br>
                <Trigger :promotion="promotion" :tags="formattedTags" />
                <br>
                <Activation :promotion="promotion" @hasCode="hasCode = $event" />
              </div>
              <div class="column">
                <Tags :promotion="promotion" :tags="formattedTags" />
                <br>
                <UsageRestriction :promotion="promotion" />
              </div>
            </template>
            <template v-if="isProductDiscount">
              <div class="column">
                <Reward :promotion="promotion" :currencySymbol="currencySymbol" :showReplace="true" />
                <br>
                <MinimumAmount :promotion="promotion" :currencySymbol="currencySymbol" />
                <br>
                <Trigger :promotion="promotion" :tags="formattedTags" />
                <br>
                <Activation :promotion="promotion" @hasCode="hasCode = $event" />
              </div>
              <div class="column">
                <Tags :promotion="promotion" :tags="formattedTags" />
                <br>
                <UsageRestriction :promotion="promotion" />
                <br>
                <Application :promotion="promotion" />
              </div>
            </template>
            <template v-if="isOrderDiscount">
              <div class="column">
                <Reward :promotion="promotion" :currencySymbol="currencySymbol" :showReplace="false" />
                <br>
                <MinimumAmount :promotion="promotion" :currencySymbol="currencySymbol" />
                <br>
                <Activation :promotion="promotion" @hasCode="hasCode = $event" />
              </div>
              <div class="column">
                <Tags :promotion="promotion" :onlyExclusion="true" :tags="formattedTags" />
                <br>
                <Trigger :promotion="promotion" :tags="formattedTags" />
                <br>
                <UsageRestriction :promotion="promotion" />
              </div>
              <br>
            </template>
          </div>
        </TabSection>

        <TabSection :title="$t('promotion_new.availability')">
          <div class="subtitle">{{$t('promotion_new.time_frame')}}</div>
          <label>{{$t('promotion_new.availability_subtitle')}}</label>
          <br>
          <div class="columns">
            <div class="column">
              <Start :promotion="promotion" />
            </div>
            <div class="column">
              <End :promotion="promotion" />
            </div>
          </div>

          <div class="separator"></div>
          <div class="subtitle">{{ $t('promotion_new.order_types') }}</div>
          <label>{{ $t('promotion_new.order_types_subtitle') }}</label>
          <br>
          <DisableFor :disableFor="disableFor" />

          <div class="separator"></div>
          <div class="subtitle">{{ $t('promotion_new.weekly_schedule') }}</div>
          <label>{{ $t('promotion_new.weekly_schedule_subtitle') }}</label>
          <br>
          <Schedule :promotion="promotion" />

          <template v-if="branchCompanies.length > 0 && !companyId">
            <div class="separator"></div>
            <div class="subtitle">{{ $t('promotion_new.companies') }}</div>
            <label>{{ $t('promotion_new.companies_subtitle') }}</label>
            <br>
            <Companies :companies="branchCompanies" :inventory="inventory" :promotion="promotion" />
          </template>

        </TabSection>

        <TabSection :title="$t('promotion_new.marketing')">
          <div class="subtitle">{{ $t('promotion_new.presentation') }}</div>
          <label>{{ $t('promotion_new.presentation_subtitle') }}</label>
          <br>
          <div class="columns">
            <div class="column">
              <div class="group">
                <LocalizedInput :title="$t('name')" :translate="promotion.name"></LocalizedInput>
              </div>
              <br>
              <div class="group">
                <LocalizedInput :title="$t('description')" :translate="promotion.description"></LocalizedInput>
              </div>
              <br>
              <div class="group">
                <label>{{$t('external_id')}}</label>
                <input type="text" v-model="promotion.externalId">
              </div>
            </div>
            <div class="column">
              <ImageUpload identifier="promoImage" :previewHeight="110"
              :object="promotion.image" @changed="promotion.image = $event" :maxSizeInMb="0.8"></ImageUpload>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('promotion_new.usage')">
          <div class="subtitle">{{$t('promotion_new.usage_report')}}</div>
          <label>{{$t('promotion_new.usage_report_subtitle')}}</label>
          <br>
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{$t('start')}}</label>
                <datetime type="datetime" v-model="filters.start" :use12-hour="true"/>
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>{{$t('end')}}</label>
                <datetime type="datetime" v-model="filters.end" :use12-hour="true"/>
              </div>
            </div>
          </div>
          <div class="button-holder">
            <button class="btn btn-primary" @click="applyFilters(true)" v-if="!(filters.start && filters.end)">
              {{$t('promotion_usage.generate_report')}}
            </button>
          </div>
          <template v-if="filters.start && filters.end">
            <div class="separator"></div>
            <div class="subtitle">{{$t('promotion_new.total_usage')}}</div>
            <label>{{$t('promotion_new.total_usage_subtitle')}}</label>
            <br>
            <div class="stats" v-if="stats">
              <div class="stat">
                <label>{{ $t('promotion_usage.totalUsage') }}</label>
                <div class="stat__value"> {{ stats.totalUsage || "00" }}</div>
              </div>
              <div class="stat">
                <label>{{ $t('promotion_usage.totalUniqueUser') }}</label>
                <div class="stat__value"> {{ stats.totalUniqueUser || "00" }}</div>
              </div>
              <div class="stat" v-if="Object.keys(stats.totalByCurrency).length > 0">
                <label>{{ $t('promotion_usage.totalByCurrency') }}</label>
                <div class="stat__value">
                  <template v-for="(total, currency) of stats.totalByCurrency">
                    {{total}} {{currency.toUpperCase()}}
                  </template>
                </div>
              </div>
            </div>
            <div class="separator"></div>
            <div class="subtitle">{{$t('promotion_new.promotion_usage')}}</div>
            <label>{{$t('promotion_new.promotion_usage_subtitle')}}</label>
            <br>
            <Datatable entity="promotion_usage_by_promo" class="datatable" ref="datatable"
                       v-if="filters.start && filters.end" :parameters="datatableParameters" :onRowClick="onRowClick"
                       :addShowAll="true" :rowActions="[{text: 'datatable.view_sale', method: onRowClick}]" />
          </template>
        </TabSection>
      </Tabs>
    </div>

    <PageFooter :backAction="{text: $t('promotion_new.back'), action: back}" v-if="$store.getters.loggedUserCanSaveInventory"
                :secondaryActions="[{text: $t('save'), action: save}]"
                :mainAction="{text: $t('save_and_quit'), action: saveAndQuit}">
    </PageFooter>

    <Modal name="delete-confirmation" :title="$t('promotions.confirmation')" dismissable
        :actions="[{ name: $t('back'), action: 'close' },
                   { name: $t('datatable.delete'), action: deletePromotionAction, primary: true }]">
      <p>{{ $t('promotions.delete_message') }}?</p>
    </Modal>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import moment from "moment-timezone";
  import { Core } from "@/core/Core";
  import PageTitle from "@/components/PageTitle";
  import PageFooter from "@/components/PageFooter";
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import ImageUpload from '@/components/tools/ImageUpload';
  import XYParameter from "@/components/promotion/XYParameters";
  import Activation from "@/components/promotion/Activation";
  import Application from "@/components/promotion/Application";
  import Tags from "@/components/promotion/Tags";
  import UsageRestriction from "@/components/promotion/UsageRestriction";
  import MinimumAmount from "@/components/promotion/MinimumAmount";
  import Trigger from "@/components/promotion/Trigger";
  import Reward from "@/components/promotion/Reward";
  import Start from "@/components/promotion/Start";
  import End from "@/components/promotion/End";
  import DisableFor from "@/components/promotion/DisableFor";
  import Schedule from "@/components/promotion/Schedule";
  import Companies from "@/components/promotion/Companies";
  import PromotionType from "@/components/promotion/PromotionType";
  import Datatable from "@/components/Datatable";

  export default {
    name: "PromotionEdit",

    components: {
      PageTitle, PageFooter, LocalizedInput, ImageUpload,
      Activation, XYParameter, Tags, UsageRestriction, MinimumAmount, PromotionType,
      Trigger, Reward, Start, End, DisableFor, Schedule, Companies, Application, Datatable
    },

    data() {
      return {
        X_FOR_Y: "x_for_y",
        PRODUCT_ACCESS: "product_access",
        PRODUCT_DISCOUNT: "product_discount",
        ORDER_DISCOUNT: "order_discount",
        tags: [],
        branchCompanies: [],
        currencySymbol: "",
        promoType: null,
        hasCode: false,
        disableFor: {
          delivery: false,
          takeout: false
        },
        promotion: null,
        filters: {
          start: null,
          end: null
        },
        inventory: null,
        stats: {
          totalByCurrency: {},
          totalUsage: "",
          totalUniqueUser: ""
        }
      }
    },

    computed: {
      id() {
        return this.$route.query.id;
      },
      branchId() {
        return this.$route.query.branch;
      },
      companyId() {
        return this.$route.query.company;
      },
      datatableParameters() {
        return {
          promotion: this.id,
          start: this.filters.start,
          end: this.filters.end
        }
      },
      isXforY() {
        return this.promoType === this.X_FOR_Y;
      },
      isProductAccess() {
        return this.promoType === this.PRODUCT_ACCESS;
      },
      isProductDiscount() {
        return this.promoType === this.PRODUCT_DISCOUNT;
      },
      isOrderDiscount() {
        return this.promoType ===  this.ORDER_DISCOUNT;
      },
      isPromotionScheduleEmpty() {
        if (!this.promotion.schedule) {
          return true;
        }
        let notEmptyDays = this.promotion.schedule.filter(d =>
            d.hours.length > 0 && d.hours.filter(h => !!h.from || !!h.to).length > 0);
        return notEmptyDays.length === 0;
      },
      formattedTags() {
        return this.tags.map(t => {
          return {
            id: t.id,
            label: t.name,
            value: t.id
          };
        });
      }
    },

    watch: {
      "filters.start"() {
        this.applyFilters();
      },
      "filters.end"() {
        this.applyFilters();
      }
    },

    async mounted() {
      if (!this.id) {
        this.$router.push(this.$router.buildContext('/promotions'));
        return;
      }
      showSpinner();
      this.$store.state.pageTitleTranslationKey = "promotions.title";
      this.$store.state.pageIcon = "/icon/promos.svg";
      this.inventory = await this.$store.getters.getContextInventory;
      this.currencySymbol = await this.$store.getters.getContextCurrencySymbol;
      await this.loadPromotion();
      await this.loadBranchCompanies();
      hideSpinner();
    },

    methods: {
      ...mapActions(["getPromotion", "getManyInventoryEntity", "savePromotion", "deletePromotion", "statsOfPromotion"]),

      async loadPromotion() {
        let data = await this.getPromotion({inventory: this.inventory.id, id: this.id});
        if (!data.version) {
          //Go to legacy page
          this.$router.push({path: '/promotions/legacy-edit', query: this.$route.query});
          return;
        }
        //Load tags from inventory data
        this.tags = await this.getManyInventoryEntity({entity: "tags", inventory: this.inventory.id});
        //Promotion data formatting
        if (data.startAt) {
          data.startAt = moment(data.startAt, "YYYY-MM-DD HH:mm:ss").format();
        }
        if (data.endAt) {
          data.endAt = moment(data.endAt, "YYYY-MM-DD HH:mm:ss").format();
        }
        data.inclusion.tags = this.formatTags(data.inclusion.tags);
        data.exclusion.tags = this.formatTags(data.exclusion.tags);
        data.triggerParameters.tags = this.formatTags(data.triggerParameters.tags);
        this.disableFor.takeout = data.disableForMethod.indexOf("takeout") > -1;
        this.disableFor.delivery = data.disableForMethod.indexOf("delivery") > -1;
        this.promotion = data;
        this.detectPromotionType();
      },
      formatTags(object) {
        let foundTags = object ? this.tags.filter(t => object.indexOf(t.id) > -1) : [];
        return this.formatObjectsForSelect(foundTags);
      },
      async loadBranchCompanies() {
        if (this.companyId) {
          this.branchCompanies = [];
          this.promotion.companies = [];
        } else {
          this.branchCompanies = await Core.request('GET', '/api/entity/company/select', {branch: this.branchId});
          if (this.branchCompanies.length > 0) {
            this.branchCompanies = this.branchCompanies.filter(c => c.inventory == this.inventory.id);
          }
          this.promotion.companies = this.branchCompanies.filter(c => this.promotion.companies.indexOf(c.id) > -1);
        }
      },
      formatObjectsForSelect(array) {
        return array.map(t => {
          return {
            id: t.id,
            label: t.name,
            value: t.id
          }
        });
      },
      _formatBeforeSave(data) {
        let copy = deepCopy(data);

        copy.image = getImageData(data.image);
        if (copy.startAt) {
          copy.startAt = moment(copy.startAt).format("YYYY-MM-DD HH:mm:ss");
        }
        if (copy.endAt) {
          copy.endAt = moment(copy.endAt).format("YYYY-MM-DD HH:mm:ss");
        }
        if (!copy.schedule || this.isPromotionScheduleEmpty) {
          copy.schedule = null;
        }
        if (copy.inclusion.tags) {
          copy.inclusion.tags = copy.inclusion.tags.map(x => x.id);
        }
        if (copy.exclusion.tags) {
          copy.exclusion.tags = copy.exclusion.tags.map(x => x.id);
        }
        if (copy.rewardType === "hidden_product") {
          copy.rewardAmount = 0;
        }
        copy.minAmount = copy.minAmount || 0;
        if (copy.triggerParameters.x) {
          copy.triggerParameters.x = parseInt(copy.triggerParameters.x);
        }
        if (!this.companyId) {
          copy.companies = copy.companies.map(x => x.id);
        } else {
          delete copy.companies;
        }
        if (copy.triggerParameters.y) {
          copy.triggerParameters.y = parseInt(copy.triggerParameters.y);
        }
        if (copy.triggerParameters.items) {
          copy.triggerParameters.items = copy.triggerParameters.items.map(x => x.id);
        }
        if (copy.triggerParameters.tags) {
          copy.triggerParameters.tags = copy.triggerParameters.tags.map(x => x.id);
        }
        copy.rewardAmount = parseFloat(String(copy.rewardAmount).replace(",", "."));
        // Disable for methods
        copy.disableForMethod = [];
        if (this.disableFor.takeout) {
          copy.disableForMethod.push("takeout");
        }
        if (this.disableFor.delivery) {
          copy.disableForMethod.push("delivery");
        }
        return copy;
      },
      fixDateTime(datetime) {
        if (!datetime) {
          return null
        }
        return datetime.replace('T', ' ').substr(0, 19)
      },
      detectPromotionType() {
        if (this.promotion.triggerType === "x_for_y") {
          this.promoType = this.X_FOR_Y;
        } else if (this.promotion.rewardType === "hidden_product") {
          this.promoType = this.PRODUCT_ACCESS;
        } else if (this.promotion.appliedOn === "items" || this.promotion.appliedOn === "one_item") {
          this.promoType = this.PRODUCT_DISCOUNT;
        } else if (this.promotion.appliedOn === "sale") {
          this.promoType = this.ORDER_DISCOUNT;
        }
      },
      setAsXforY() {
        this.promotion.rewardType = "%";
        this.promotion.rewardAmount = "100";
        this.promotion.triggerType = "x_for_y";
        this.promotion.appliedOn = "items"; //items or one_item
        this.promotion.triggerParameters.x = 2;
        this.promotion.triggerParameters.y = 1;
        this.promotion.inclusion.tags = [];
        this.promotion.exclusion.tags = [];
        this.promotion.triggerParameters.tags = [];

        this.promoType = this.X_FOR_Y;
      },
      setAsProductAccess() {
        this.promotion.appliedOn = "items";
        this.promotion.triggerType = ""; //mobile or 'with_item'
        this.promotion.inclusion.tags = [];
        this.promotion.exclusion.tags = [];
        this.promotion.rewardType = "hidden_product";
        this.promotion.triggerParameters.tags = [];

        this.promoType = this.PRODUCT_ACCESS;
      },
      setAsProductDiscount() {
        this.promotion.appliedOn = "items"; //items or one_item
        this.promotion.rewardAmount = "";
        this.promotion.rewardType = "";
        this.promotion.triggerType = ""; //mobile or 'with_item'
        this.promotion.inclusion.tags = [];
        this.promotion.exclusion.tags = [];
        this.promotion.triggerParameters.tags = [];

        this.promoType = this.PRODUCT_DISCOUNT;
      },
      setAsOrderDiscount() {
        this.promotion.appliedOn = "sale";
        this.promotion.triggerType = ""; //mobile or 'with_item'
        this.promotion.rewardAmount = "";
        this.promotion.rewardType = "";
        this.promotion.inclusion.tags = [];
        this.promotion.exclusion.tags = [];
        this.promotion.triggerParameters.tags = [];

        this.promoType = this.ORDER_DISCOUNT;
      },
      deletePromo() {
        this.$modal.show("delete-confirmation");
      },
      async applyFilters(showError) {
        if (!(this.filters.start && this.filters.end)) {
          if (showError) {
            toast(this.$t('promotion_usage.invalid'), "error");
          }
          return;
        }
        /* doesn't need to show spinner, because already showing from the datatable update ...*/
        let response = await this.statsOfPromotion({
          inventory: this.inventory.id,
          id: this.id,
          filters: {
            start: this.fixDateTime(this.filters.start),
            end: this.fixDateTime(this.filters.end)
          }
        });
        if (response) {
          this.stats = response.stats;
        }
      },
      async deletePromotionAction() {
        showSpinner();
        let response = await this.deletePromotion({inventory: this.inventory.id, id: this.promotion.id });
        hideSpinner();
        if (response) {
          this.$modal.show("delete-confirmation", false);
          this.$router.push(this.$router.buildContext("/promotions"));
        } else {
          this.$modal.show("delete-confirmation", false);
          toast(this.$t("error.no_server"),"error");
        }
      },
      saveAndQuit() {
        this.save(true);
      },
      async save(exit) {
        let copy = this._formatBeforeSave(this.promotion);
        showSpinner();
        let response = await this.savePromotion({ inventory: this.inventory.id, id: copy.id, data: copy });
        hideSpinner();
        if (!response) {
          toast(this.$t("promotions.error.save"), "error");
          return;
        }
        toast(this.$t("promotions.success"), "success");
        if (exit) {
          this.goToPromotions();
        }
      },
      back() {
        this.goToPromotions();
      },
      goToPromotions() {
        this.$router.push(this.$router.buildContext("/promotions"));
      },
      onRowClick(row) {
        this.$router.push({ path: '/sale', query : { id : row.saleNumber, branch: this.branchId, company: this.companyId }});
      },
    }
  }
</script>

<style lang="scss" scoped>
.subtitle {
  margin-bottom: 5px;
}
.promotion-types {
  display: flex;
  padding-top: 10px;
  justify-content: space-between;

  .promotion-type {
    &:not(:last-child) {
      margin-right: 10px;
    }
  }
}

.stats {
  display: flex;
  padding-top: 10px;
  justify-content: flex-start;

  .stat {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    min-width: 250px;
    padding: 16px;

    .stat__value {
      font-weight: bold;
      color: var(--primary-color);
      font-size: 40px;
      line-height: 1;
      margin-top: 2px;
    }

    &:not(:last-child) {
      margin-right: 10px;
    }
  }
}

.separator {
  width: 100%;
  height: 1px;
  border-top: 1px dashed #e6e6e6;
  margin: 24px 0;
}
</style>

<style lang="scss">
#promotionEdit {
  .columns {
    flex-wrap: wrap;
  }
  .tableContainer {
    padding: 0;
  }
  .promotion-type {
    padding-right: 24px;
    .promotion-type__name {
      font-size: 16px;
    }
    .promotion-type__description {
      font-size: 12px;
    }
    .promotion-type__checkbox {
      top: 8px;
      right: 8px;
    }
  }
}

@media screen and (max-width: 800px) {
  #promotionEdit {
    .promotion-types {
      flex-direction: column;

      .promotion-type {
        margin-bottom: 10px;
      }
    }
    .columns .column {
      &:first-child {
        margin-bottom: 20px;
      }
    }
    .stats {
      flex-direction: column;
      .stat {
        margin-right: 0;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
